<template>
  <div class="container">
    <div class="row">
      <div class="col-md-8 col-md-offset-2">
        <div class="bsd-section">
          <breadcrumb />
        </div>

        <div class="bsd-section">
          <alert-box ref="alertBox" class="spacer-l" />

          <template v-if="error">
            <router-link
              tag="button"
              :to="{ name: 'panel'}"
              class="z-button z-button--secondary z-button--block"
            >
              Voltar para página inicial
            </router-link>
          </template>
        </div>

        <boleto-loader v-if="loading" />

        <boleto-generator
          v-else-if="!error"
          :title="`Mensalidade de ${billTitle}`"
          :title-on-ready="`Copie o código ou baixe o boleto para pagar a mensalidade de ${bill.month_name}`"
          :payment="billPayment"
          :ready="boletoReady"
          :waiting="boletoWaiting"
          :unavailable="boletoUnavailable"
          :callback-clipboard="copySuccess"
          :handle-download-boleto="downloadBoleto"
          :handle-generate-boleto="createBillPayment"
        >
          <price-card :full-value="parseFloat(bill.full_value)" :due-date="bill.due_date" />
        </boleto-generator>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.payment-code {
  color: var(--color-neutral-black);
  font-size: rem(16);
  line-height: rem(24);
  margin-bottom: var(--space-extra-large);
}
</style>

<script>
import { mapState } from 'vuex';
import AlertBox from 'components/zilla/alert-box';
import Breadcrumb from 'components/breadcrumb';
import DashboardService from 'services/DashboardService';
import BoletoService from 'services/BoletoService';
import DateUtils from 'utils/date-utils';
import PriceCard from 'components/price-card';
import BoletoGenerator from 'pages/shared/boleto/boleto-generator';
import BoletoLoader from './boleto-loader';

export default {
  components: {
    AlertBox,
    BoletoLoader,
    BoletoGenerator,
    Breadcrumb,
    PriceCard,
  },
  data() {
    return {
      bill: {},
      billPayment: {},
      error: false,
      loading: true,
      boletoUnavailable: false,
      boletoWaiting: false,
      boletoReady: false,
    };
  },
  computed: {
    ...mapState([
      'currentBilling',
    ]),
    dueDate() {
      return DateUtils.getFormattedDate(this.bill.due_date);
    },
    billingId() {
      return this.currentBilling.id;
    },
    billId() {
      return this.$route.params.billId;
    },
    reenrollmentBill() {
      return this.bill.metadata && this.bill.metadata.reenrollment_id;
    },
    billTitle() {
      if (this.reenrollmentBill) return `${this.bill.month_name} (Rematrícula)`;

      return this.bill.month_name;
    },
  },
  methods: {
    loadBill() {
      return DashboardService.getBill(this.billId).then((bill) => {
        this.bill = bill;
        this.$tracker.logEvent('Viewed Boleto Page', { billId: this.bill.id });
      });
    },
    loadBillPayment() {
      this.treatPromise(BoletoService.getBoletoBillPayment(this.billingId, this.billId));
    },
    createBillPayment() {
      this.boletoWaiting = true;
      this.boletoUnavailable = false;

      this.setLoadingTimeout();
      this.treatPromise(BoletoService.createBoletoBillPayment(this.billingId, this.billId));
    },
    pollForBillPayment() {
      this.setLoadingTimeout();
      this.treatPromise(BoletoService.tryBoletoBillPayment(this.billingId, this.billId));
    },
    validateBillPayment() {
      this.resetBoletoStatus();

      if (this.bill && this.bill.status === 'paid') {
        this.$router.push({ name: 'payment-receipt', params: { billId: this.bill.id } });
      } else if (this.billPayment === null) {
        this.boletoUnavailable = true;
      } else if (this.billPayment.bar_code === null) {
        this.boletoWaiting = true;
        this.pollForBillPayment();
      } else {
        this.boletoReady = true;
      }
    },
    treatPromise(promise) {
      promise
        .then((data) => {
          this.billPayment = data;
          this.validateBillPayment();
          this.loading = false;
        })
        .catch((error) => {
          this.showErrorMessage();
          this.$rollbar.error(error);
        })
        .finally(this.hideAlert);
    },
    downloadBoleto() {
      this.$pageLoader.show('Baixando boleto');
      const billDate = `${this.bill.month_name} de ${this.bill.bill_year}`;
      const courseName = this.currentBilling.course_name;
      const institutionName = this.currentBilling.institution_name;
      const fileName = `Mensalidade de ${billDate} - ${courseName} - ${institutionName}`;
      BoletoService.downloadBoleto(this.billPayment.id, fileName)
        .catch((error) => {
          this.showErrorMessage();
          this.$rollbar.error(`Erro ao fazer download do boleto: ${error}`);
        })
        .finally(() => this.$pageLoader.hide());
      this.$tracker.logEvent('[boleto] Clicked Download');

      if (this.originFromReminderHSM()) {
        this.$tracker.logEvent('[Boleto] Download boleto from reminder hsm');
      }
    },
    copySuccess() {
      this.$tracker.logEvent('[boleto] Copied to clipboard');
    },
    setLoadingTimeout() {
      setTimeout(() => {
        if (this.boletoWaiting) {
          this.$refs.alertBox.show(
            'warning', 'O boleto está sendo processado',
            'Isso pode demorar alguns minutos, por favor volte mais tarde.',
          );
        }
      }, 10000);
    },
    showErrorMessage() {
      this.error = true;
      this.$refs.alertBox.show(
        'danger', 'Erro inesperado',
        'Não foi possível gerar o boleto. Tente novamente mais tarde ou entre em contato com o Quero Pago.',
      );
    },
    resetBoletoStatus() {
      this.boletoUnavailable = false;
      this.boletoWaiting = false;
      this.boletoReady = false;
    },
    hideAlert() {
      if (this.$refs.alertBox) {
        this.$refs.alertBox.hide();
      }
    },
    originFromReminderHSM() {
      if (this.$route.query) {
        return this.$route.query.from === 'reminder';
      }
      return false;
    },
  },
  mounted() {
    this.loadBill().then(this.loadBillPayment).catch(this.showErrorMessage);
  },
  beforeMount() {
    if (this.originFromReminderHSM()) {
      this.$tracker.logEvent('[Boleto] Open link from reminder hsm');
    }
  },
};
</script>
